import {
  createClient,
  type SupabaseClient,
  type SupabaseClientOptions,
} from '@supabase/supabase-js';
import type { Database } from '@kmgestao/db-types';

/** Cliente Supabase tipado contra o schema Postgres do KmGestao. */
export type KmgestaoSupabaseClient = SupabaseClient<Database>;

export interface CreateSupabaseClientInput {
  url: string;
  anonKey: string;
  /** Overrides para `SupabaseClientOptions` (ex: storageKey custom, fetch). */
  options?: SupabaseClientOptions<'public'>;
}

/**
 * Cria um cliente Supabase tipado. O consumidor é responsável por ler as
 * envs (`VITE_SUPABASE_URL`, `VITE_SUPABASE_ANON_KEY`) e criar um singleton.
 *
 * Defaults aplicados:
 * - `persistSession: true` — mantém sessão entre reloads (localStorage)
 * - `autoRefreshToken: true` — renovação automática antes do expiry
 * - `detectSessionInUrl: true` — necessário para callback de OAuth e magic link
 * - `storageKey: 'kmgestao.auth'` — evita colisão com outros apps no mesmo domínio
 * - `flowType: 'pkce'` — mais seguro que o implicit flow legado
 *
 * @example
 * // apps/web/src/lib/supabase.ts
 * export const supabase = createSupabaseClient({
 *   url: import.meta.env.VITE_SUPABASE_URL,
 *   anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
 * });
 */
export function createSupabaseClient({
  url,
  anonKey,
  options,
}: CreateSupabaseClientInput): KmgestaoSupabaseClient {
  if (!url || !anonKey) {
    throw new Error(
      'createSupabaseClient: url e anonKey são obrigatórios. ' +
        'Verifique VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY no .env.local.',
    );
  }
  return createClient<Database>(url, anonKey, {
    ...options,
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'kmgestao.auth',
      flowType: 'pkce',
      ...options?.auth,
    },
  });
}

// Re-exports úteis para consumidores (evita importar de @supabase/supabase-js)
export type { AuthError, AuthResponse, Session, SupabaseClient, User } from '@supabase/supabase-js';
